/**
 * NotificationToast — F 490's toast.
 *
 * WHAT IT IS
 * ----------
 * A transient card that appears when a reminder fires. It shows the
 * reminder's text, and it can be dismissed.
 *
 * WHY IT IS A TOAST AND NOT A BADGE ALONE
 * ----------------------------------------
 * The bell badge is a COUNT — it says something happened but not what. A
 * reminder that fires while the operator is looking at the page has to
 * SAY SOMETHING, because a silent badge is easy to miss and the operator
 * will believe the reminder did not fire.
 *
 * WHY IT DOES NOT AUTO-DISMISS
 * ----------------------------
 * A reminder is a thing the operator asked to be TOLD. A toast that
 * disappears on a timer can vanish before it is read, and the operator
 * never learns the reminder fired — which is exactly the failure the
 * reminder was set to prevent. So it stays until it is dismissed, and it
 * carries a "Mark read" action that also clears it.
 *
 * WHY IT IS A PORTAL
 * ------------------
 * A toast must not be clipped by an ancestor's `overflow: hidden` or
 * `z-index` stacking context. Rendering it into a portal at the document
 * root is what makes it reliably on top.
 */
import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, Group, Paper, Text } from '@mantine/core';
import { IconBell, IconX } from '@tabler/icons-react';
import type { Notification } from '../../../types/models/Reminder';
import './NotificationToast.css';

export interface NotificationToastProps {
  /** The notification to show. Null renders nothing. */
  notification: Notification | null;
  /** Called when the operator dismisses the toast. */
  onDismiss: () => void;
  /** Called when the operator marks the notification read. */
  onMarkRead?: (n: Notification) => void;
}

export const NotificationToast: React.FC<NotificationToastProps> = ({
  notification,
  onDismiss,
  onMarkRead,
}) => {
  // Escape dismisses. A toast the operator cannot get rid of is a trap.
  useEffect(() => {
    if (!notification) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [notification, onDismiss]);

  const handleMarkRead = useCallback(() => {
    if (notification) onMarkRead?.(notification);
    onDismiss();
  }, [notification, onMarkRead, onDismiss]);

  if (!notification) return null;
  if (typeof document === 'undefined') return null;

  const when = (() => {
    const d = new Date(notification.fired_at);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  })();

  return createPortal(
    <Paper
      className="fc-notification-toast"
      shadow="lg"
      p="sm"
      radius="md"
      role="status"
      aria-live="polite"
      data-testid="fc-notification-toast"
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <Box className="fc-notification-toast__icon" aria-hidden="true">
          <IconBell size={16} />
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text size="xs" fw={600} data-testid="fc-notification-toast-text">
            {notification.text}
          </Text>
          {when && (
            <Text size="10px" c="dimmed" data-testid="fc-notification-toast-time">
              Reminder · {when}
            </Text>
          )}
          <Button
            size="compact-xs"
            variant="light"
            mt={6}
            onClick={handleMarkRead}
            data-testid="fc-notification-toast-read"
          >
            Mark read
          </Button>
        </Box>
        <ActionIconX onClick={onDismiss} />
      </Group>
    </Paper>,
    document.body,
  );
};

/** A tiny local close button, so the toast does not import ActionIcon twice. */
const ActionIconX: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    className="fc-notification-toast__close"
    onClick={onClick}
    aria-label="Dismiss notification"
    data-testid="fc-notification-toast-dismiss"
  >
    <IconX size={14} />
  </button>
);
